import {
  ApiTokenListResponse,
  CurrentSubscriptionDTO,
  DetailedInvoiceDTO,
  InvoiceDTO,
  InvoiceNewDTO,
  InvoiceStatus,
  MembershipForChangeDTO,
  PaymentMethodDTO,
  PaymentMethodSetupIntent,
  SessionPackUsage,
  SubscriptionMembershipChangeDatesDTO,
  SubscriptionMembershipChangeSchedulePostDTO,
  TokenPageableRequestParams,
  UserMemberBean,
  UserMemberPost,
} from "@gymflow/types";
import axios, { AxiosInstance } from "axios";

export type ProfileApiType = ReturnType<typeof profileApi>;

const profileApi = (axiosInstance: AxiosInstance, clubId: number) => {
  const clubPrefix = `club/${clubId}/` as const;

  return {
    create(newResource: any) {
      return axiosInstance.post(
        `${clubPrefix}customer/user/member`,
        newResource,
      );
    },
    /*** @deprecated - use updateNew instead*/
    update(_: any, clubId: number, patchedFields: any) {
      return axiosInstance.patch(
        `${clubPrefix}customer/user/member`,
        patchedFields,
      );
    },
    updateNew(patchedFields: Partial<UserMemberPost>) {
      return axiosInstance.patch(
        `${clubPrefix}customer/user/member`,
        patchedFields,
      );
    },
    get() {
      return axiosInstance.get<UserMemberBean>(
        `${clubPrefix}customer/user/member`,
      );
    },
    checkoutSessionPack(
      params: {
        sessionPackId: number;
        startDate: string;
        promotionCode?: string;
        paymentMethodId?: string;
      },
      idempotencyKey: string,
    ) {
      return axiosInstance.put<{
        invoiceNumber: string;
        invoiceStatus: InvoiceStatus;
      }>(`${clubPrefix}customer/session-pack-signup/${idempotencyKey}`, params);
    },
    checkoutMembership(
      params: {
        membershipId: number;
        startDate: string;
        promotionCode?: string;
        paymentMethodId?: string;
      },
      idempotencyKey: string,
    ) {
      return axiosInstance.put<{
        invoiceNumber: string;
        invoiceStatus: InvoiceStatus;
      }>(`${clubPrefix}customer/membership-signup/${idempotencyKey}`, params);
    },

    findById() {
      return axiosInstance.get(`${clubPrefix}customer/user/member`);
    },
    async updatePictureNew(blob: globalThis.Blob) {
      const formData = new FormData();
      formData.append("file", blob, "image");
      return axiosInstance.post(
        `${clubPrefix}customer/user/member/picture`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
    },
    /*** @deprecated - use updateNew instead */
    async updatePicture(_: any, { blob, name }: any) {
      const blobResponse = await axios.get(blob, { responseType: "blob" });
      const formData = new FormData();
      formData.append("file", blobResponse.data, name);
      return axiosInstance.post(
        `${clubPrefix}customer/user/member/picture`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
    },

    async fetchPaymentMethods(_: string, clubId: number) {
      const result = await axiosInstance.get<PaymentMethodDTO[]>(
        `${clubPrefix}customer/payment-method`,
      );
      (result as any).data = (result as any).data.map((e: any) => ({
        ...e,
        id: e.paymentMethodId,
      }));
      return result;
    },

    removePaymentMethod(_: string, clubId: number, paymentMethodId: string) {
      return axiosInstance.delete(
        `${clubPrefix}customer/payment-method/${paymentMethodId}`,
      );
    },

    assignDefaultPaymentMethod(
      _: string | undefined,
      __: number | undefined,
      paymentMethodId: string,
    ) {
      return axiosInstance.put(
        `${clubPrefix}customer/payment-method/default/${paymentMethodId}`,
      );
    },

    resumeSubscription(_: string, subscriptionId: number) {
      return axiosInstance.post(
        `${clubPrefix}customer/user/member/service/resume/${subscriptionId}`,
      );
    },

    pauseSubscription(_: string, subscriptionId: number) {
      return axiosInstance.post(
        `${clubPrefix}customer/user/member/service/pause/${subscriptionId}`,
      );
    },

    cancelSubscription(_: string, subscriptionId: number) {
      return axiosInstance.post(
        `${clubPrefix}customer/user/member/service/cancel/${subscriptionId}`,
      );
    },

    payPastDueInvoice(invoiceNumber: string, paymentMethodId?: string) {
      return axiosInstance.put<InvoiceDTO>(
        `${clubPrefix}customer/invoice/${invoiceNumber}/pay-past-due/${paymentMethodId}`,
      );
    },
    getInvoiceByInvoiceNumber(invoiceNumber: string) {
      return axiosInstance.get<DetailedInvoiceDTO>(
        `${clubPrefix}customer/invoice/${invoiceNumber}`,
      );
    },

    invoiceFile(clubId: number, invoiceNumber: string) {
      const url =
        `${clubPrefix}customer/user/member/invoice-file/:invoiceNumber`.replace(
          ":invoiceNumber",
          invoiceNumber,
        );

      return axiosInstance.get(url);
    },
    updateEmailAndPassword(payload: any) {
      const url = `${clubPrefix}customer/user/member/account`;
      return axiosInstance.patch(url, payload);
    },
    highlightedInvoice() {
      return axiosInstance.get<DetailedInvoiceDTO>(
        `${clubPrefix}customer/user-member-invoice-list/highlighted`,
      );
    },
    findInvoicesNew({ ...params }: TokenPageableRequestParams) {
      return axiosInstance.get<ApiTokenListResponse<InvoiceNewDTO>>(
        `${clubPrefix}customer/user-member-invoice-list/`,
        { params },
      );
    },
    getPaymentMethodSetupIntent() {
      return axiosInstance.put<PaymentMethodSetupIntent>(
        `${clubPrefix}customer/payment-method/setup-intent`,
      );
    },
    attachAuthorizedSetupIntent(setupIntentId: string) {
      return axiosInstance.put<{}>(
        `${clubPrefix}customer/payment-method/setup-intent-authorized/${setupIntentId}`,
      );
    },
    sessionPackUsage(
      params: { sessionCost: number } & (
        | { appointableCategoryId: number }
        | { activityCategoryId: number }
      ),
    ) {
      return axiosInstance.put<SessionPackUsage>(
        `${clubPrefix}customer/user/member/session-pack-usage`,
        params,
      );
    },
    accountDelete() {
      return axiosInstance.post(
        `${clubPrefix}customer/user/member/account-delete`,
        {
          clubId,
        },
      );
    },
    getCurrentMembershipSubscription() {
      return axiosInstance.get<CurrentSubscriptionDTO>(
        `${clubPrefix}customer/current-membership-subscription`,
      );
    },
    subscriptionChangeAvailableMemberships({
      subscriptionId,
      ...params
    }: {
      subscriptionId: number;
    } & TokenPageableRequestParams) {
      return axiosInstance.get<ApiTokenListResponse<MembershipForChangeDTO>>(
        `${clubPrefix}customer/subscription/${subscriptionId}/change/available-memberships`,
        { params },
      );
    },
    subscriptionChangeCalculateScheduleDates({
      subscriptionId,
    }: {
      subscriptionId: number;
    }) {
      return axiosInstance.get<SubscriptionMembershipChangeDatesDTO>(
        `${clubPrefix}customer/subscription/${subscriptionId}/change/calculate-schedule-dates`,
      );
    },
    subscriptionChangeSchedule({
      params,
      subscriptionId,
    }: {
      subscriptionId: number;
      params: SubscriptionMembershipChangeSchedulePostDTO;
    }) {
      return axiosInstance.post(
        `${clubPrefix}customer/subscription/${subscriptionId}/change/schedule`,
        params,
      );
    },
    subscriptionChangeUnschedule({
      subscriptionId,
    }: {
      subscriptionId: number;
    }) {
      return axiosInstance.delete(
        `${clubPrefix}customer/subscription/${subscriptionId}/change/unschedule`,
      );
    },
  };
};

export default profileApi;
